import React, { useState } from 'react';
import { Table, Copy, Trash2, Plus, Minus } from 'lucide-react';

const MarkdownTableGeneratorTool = () => {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [data, setData] = useState(
    Array(3).fill(null).map(() => Array(3).fill(''))
  );
  const [headers, setHeaders] = useState(['Header 1', 'Header 2', 'Header 3']);
  const [alignment, setAlignment] = useState(['left', 'left', 'left']);
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const updateDimensions = (newRows, newCols) => {
    const newData = Array(newRows).fill(null).map((_, rowIdx) =>
      Array(newCols).fill(null).map((_, colIdx) =>
        data[rowIdx]?.[colIdx] || ''
      )
    );
    const newHeaders = Array(newCols).fill(null).map((_, i) =>
      headers[i] || `Header ${i + 1}`
    );
    const newAlignment = Array(newCols).fill(null).map((_, i) =>
      alignment[i] || 'left'
    );
    
    setRows(newRows);
    setCols(newCols);
    setData(newData);
    setHeaders(newHeaders);
    setAlignment(newAlignment);
  };

  const updateCell = (row, col, value) => {
    const newData = [...data];
    newData[row][col] = value;
    setData(newData);
  };

  const updateHeader = (col, value) => {
    const newHeaders = [...headers];
    newHeaders[col] = value;
    setHeaders(newHeaders);
  };

  const updateAlignment = (col, value) => {
    const newAlignment = [...alignment];
    newAlignment[col] = value;
    setAlignment(newAlignment);
  };

  const generateMarkdown = () => {
    // Generate header row
    const headerRow = `| ${headers.join(' | ')} |`;
    
    // Generate separator row with alignment
    const separator = `| ${alignment.map(a => {
      switch (a) {
        case 'left': return ':---';
        case 'right': return '---:';
        case 'center': return ':---:';
        default: return '---';
      }
    }).join(' | ')} |`;
    
    // Generate data rows
    const dataRows = data.map(row => `| ${row.join(' | ')} |`);
    
    setOutput([headerRow, separator, ...dataRows].join('\n'));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadSample = () => {
    setRows(3);
    setCols(4);
    setHeaders(['Feature', 'Basic', 'Pro', 'Enterprise']);
    setData([
      ['Storage', '5 GB', '50 GB', 'Unlimited'],
      ['Users', '1', '10', 'Unlimited'],
      ['Support', 'Email', 'Priority', '24/7 Phone']
    ]);
    setAlignment(['left', 'center', 'center', 'center']);
  };

  const clearAll = () => {
    setRows(3);
    setCols(3);
    setData(Array(3).fill(null).map(() => Array(3).fill('')));
    setHeaders(['Header 1', 'Header 2', 'Header 3']);
    setAlignment(['left', 'left', 'left']);
    setOutput('');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">Rows:</span>
            <button
              onClick={() => rows > 1 && updateDimensions(rows - 1, cols)}
              className="p-1 bg-gray-700 hover:bg-gray-600 rounded"
            >
              <Minus size={16} />
            </button>
            <span className="w-8 text-center">{rows}</span>
            <button
              onClick={() => updateDimensions(rows + 1, cols)}
              className="p-1 bg-gray-700 hover:bg-gray-600 rounded"
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">Cols:</span>
            <button
              onClick={() => cols > 1 && updateDimensions(rows, cols - 1)}
              className="p-1 bg-gray-700 hover:bg-gray-600 rounded"
            >
              <Minus size={16} />
            </button>
            <span className="w-8 text-center">{cols}</span>
            <button
              onClick={() => updateDimensions(rows, cols + 1)}
              className="p-1 bg-gray-700 hover:bg-gray-600 rounded"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
        <button
          onClick={loadSample}
          className="text-sm text-blue-400 hover:text-blue-300"
        >
          Load Sample
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {headers.map((header, i) => (
                <th key={i} className="p-1">
                  <input
                    type="text"
                    value={header}
                    onChange={(e) => updateHeader(i, e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm font-medium focus:border-blue-500"
                  />
                </th>
              ))}
            </tr>
            <tr>
              {alignment.map((align, i) => (
                <th key={i} className="p-1">
                  <select
                    value={align}
                    onChange={(e) => updateAlignment(i, e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-400 focus:border-blue-500"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, colIdx) => (
                  <td key={colIdx} className="p-1">
                    <input
                      type="text"
                      value={cell}
                      onChange={(e) => updateCell(rowIdx, colIdx, e.target.value)}
                      className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm focus:border-blue-500"
                      placeholder={`Row ${rowIdx + 1}, Col ${colIdx + 1}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        onClick={generateMarkdown}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
      >
        <Table size={18} />
        Generate Markdown
      </button>

      {output && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Markdown Output</label>
          <pre className="w-full bg-gray-800 border border-gray-700 rounded-lg p-4 text-green-400 font-mono text-sm overflow-auto">
            {output}
          </pre>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Markdown'}
        </button>
        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear All
        </button>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Preview</h3>
        {output ? (
          <div className="overflow-x-auto">
            <table className="border-collapse border border-gray-600">
              <thead>
                <tr>
                  {headers.map((h, i) => (
                    <th
                      key={i}
                      className={`border border-gray-600 px-3 py-2 bg-gray-700 text-${alignment[i]}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        className={`border border-gray-600 px-3 py-2 text-${alignment[j]}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Generate markdown to see preview</p>
        )}
      </div>
    </div>
  );
};

export default MarkdownTableGeneratorTool;
